(function () {
  'use strict';

  var STATIC_SECTIONS = ['header'];

  async function postCart(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });

    const data = await response.json();
    if (!response.ok) {
      const error = new Error(data.description || data.message || 'Cart request failed'); 
      error.cartDescription = typeof data.description === 'string' ? data.description : null;
      error.status = response.status;
      throw error;
    }
    return data;
  }
 
  function parseServerCap(description) {
    if (typeof description !== 'string') return null;
    const match = description.match(/\d+/);
    return match ? parseInt(match[0], 10) : null;
  }

  class CartPage extends HTMLElement {
    connectedCallback() {
      this.status = this.querySelector('[data-cart-status]');
      this.errorText = this.dataset.errorText || 'Something went wrong.';
      this.busy = false;

      this.sectionId = this.dataset.sectionId;
      this.changeUrl = this.dataset.cartChangeUrl;
      this.updateUrl = this.dataset.cartUpdateUrl;

      this.collapseState = {}; 
      this.serverCaps = {}; 
      this.probed = {};

      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
        if (!button) return;
        event.preventDefault();
        this.onStepperClick(button);
      });
 
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-quantity-input]');
        if (!input) return;
        const line = Number(input.dataset.line);
        const quantity = Math.max(0, parseInt(input.value, 10) || 0);
        this.updateLine(line, quantity);
      });
 
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-remove]');
        if (!remove) return;
        event.preventDefault();
        this.updateLine(Number(remove.dataset.line), 0);
      });
 
      this.addEventListener('input', (event) => {
        const note = event.target.closest('[data-cart-note]');
        if (!note) return;
        clearTimeout(this.noteTimer);
        this.noteTimer = setTimeout(() => this.updateNote(note.value), 500);
      });

    
      ['shown.bs.collapse', 'hidden.bs.collapse'].forEach((type) => {
        this.addEventListener(type, (event) => {
          const key = event.target.dataset && event.target.dataset.collapseBody;
          if (key) this.collapseState[key] = type === 'shown.bs.collapse';
        });
      });

    
      this.addEventListener('click', (event) => {
        const apply = event.target.closest('[data-discount-apply]');
        if (apply) {
          event.preventDefault();
          this.applyDiscount();
          return;
        }
        const remove = event.target.closest('[data-discount-remove]');
        if (remove) {
          event.preventDefault();
          this.removeDiscount(remove.dataset.discountCode);
        }
      });
 
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        if (!event.target.closest('[data-discount-input]')) return;
        event.preventDefault();
        this.applyDiscount();
      });

      this.watchDiscountButton();
    }

    disconnectedCallback() {
      if (this.discountObserver) this.discountObserver.disconnect();
    }

    get sections() {
      return [this.sectionId].concat(STATIC_SECTIONS);
    }

    setBusy(state) {
      this.busy = state;
      this.setAttribute('aria-busy', String(state));
      this.querySelectorAll('button[name="checkout"]').forEach((btn) => {
        btn.disabled = state;
      });
    }

    setLineBusy(line, state) {
      if (state) {
        const stepper = this.querySelector(`quantity-stepper[data-line="${line}"]`);
        const item = stepper && stepper.closest('[data-cart-item]');
        if (!item) return;
        this._busyLine = item;
        item.classList.add('cart-page-item--loading');
        item.setAttribute('aria-busy', 'true');
      } else if (this._busyLine) {
        this._busyLine.classList.remove('cart-page-item--loading');
        this._busyLine.removeAttribute('aria-busy');
        this._busyLine = null;
      }
    }

    onStepperClick(button) {
      const stepper = button.closest('[data-line]');
      const input = stepper.querySelector('[data-quantity-input]');
      const current = parseInt(input.value, 10) || 0;
      const next = button.hasAttribute('data-quantity-plus') ? current + 1 : current - 1;
      const quantity = Math.max(0, next);
      input.value = quantity;
      this.updateLine(Number(stepper.dataset.line), quantity);
    }

    async updateLine(line, quantity, retried) {
      if (this.busy || !line) return;
      let retry = false;
      this.clearLineErrors();

    
      const check = this.checkLineInventory(line, quantity); 
      const probe = !!check.probe && !retried;
      if (probe) this.probed[check.variantId] = true;
      if (!probe && check.status === 'blocked') {
        const input = this.querySelector(`[data-quantity-input][data-line="${line}"]`);
        if (input) input.value = check.cap;
        this.showLineError(line, check.message);
        return;
      }
      if (!probe && check.status === 'clamped') quantity = check.cap;

      this.setLineBusy(line, true);
      this.setBusy(true);
      try {
        const body = { line, sections: this.sections, sections_url: window.location.pathname };
        let data = await postCart(`${this.changeUrl}.js`, { ...body, quantity });
        let clamped = check.status === 'clamped';
        if (probe && quantity > check.limit) { 
          data = await postCart(`${this.changeUrl}.js`, { ...body, quantity: check.limit });
          clamped = true;
        }
        this.renderSections(data.sections);
        if (clamped) {
          this.showLineError(line, check.message, true);
        } else {
          this.announce(this.dataset.updatedText || '');
        }
      } catch (error) {
        const input = this.querySelector(`[data-quantity-input][data-line="${line}"]`);
        const previous = input ? parseInt(input.defaultValue, 10) || 0 : 0;
        if (input) input.value = input.defaultValue;

        let message = error.cartDescription || this.errorText;
        const serverCap = parseServerCap(error.cartDescription);
        if (serverCap !== null) {
          const stepper = this.querySelector(`quantity-stepper[data-line="${line}"]`);
          const variantId = stepper && stepper.dataset.variantId;
          const learned = !!variantId && this.serverCaps[variantId] !== serverCap;
          if (variantId) this.serverCaps[variantId] = serverCap;
          message =
            serverCap > 0
              ? (this.dataset.textQuantityError || '').replace('[qty]', serverCap) || message
              : this.dataset.textSoldOut || message; 
          retry = learned && !retried && quantity < previous;
        }
        if (!retry) this.showLineError(line, message);
        console.error('[cart-page]', error);
      } finally {
        this.setBusy(false);
        this.setLineBusy(line, false);
        if (retry) this.updateLine(line, quantity, true);
      }
    }

   
      
    checkLineInventory(line, quantity) {
      const stepper = this.querySelector(`quantity-stepper[data-line="${line}"]`);
      if (!stepper) return { status: 'ok' };

      let available = parseInt(stepper.dataset.inventoryQuantity, 10);
      if (stepper.dataset.inventoryTracked !== 'true') available = NaN; 
      const serverCap = this.serverCaps[stepper.dataset.variantId];
      if (typeof serverCap === 'number') {
        available = Number.isNaN(available) ? serverCap : Math.min(available, serverCap);
      }
      if (Number.isNaN(available)) return { status: 'ok' };

      const input = stepper.querySelector('[data-quantity-input]');
      const previous = input ? parseInt(input.defaultValue, 10) || 0 : 0;

      let others = 0;
      this.querySelectorAll(
        `quantity-stepper[data-variant-id="${stepper.dataset.variantId}"]`
      ).forEach((sibling) => {
        if (sibling === stepper) return;
        const siblingInput = sibling.querySelector('[data-quantity-input]');
        others += (siblingInput && parseInt(siblingInput.defaultValue, 10)) || 0;
      });
      const cap = Math.max(0, available - others);
      if (quantity <= cap) return { status: 'ok' };

      const message =
        available <= 0
          ? this.dataset.textSoldOut || this.errorText
          : (this.dataset.textQuantityError || '').replace('[qty]', available) || this.errorText;

      const variantId = stepper.dataset.variantId;
      const probe = typeof serverCap !== 'number' && !this.probed[variantId] && quantity > previous;
      const result = { cap, limit: cap, message, variantId, probe }; 
      if (quantity < previous) return { ...result, status: 'clamped' };
      if (cap <= previous) return { ...result, status: 'blocked', cap: previous };
      return { ...result, status: 'clamped' };
    }

    showLineError(line, message, isWarning) {
      if (!message) return;
      const stepper = this.querySelector(`quantity-stepper[data-line="${line}"]`);
      const item = stepper && stepper.closest('[data-cart-item]');
      const errorEl = item && item.querySelector('[data-line-error]');
      if (errorEl) {
        errorEl.classList.toggle('cart-page-item__error--warning', !!isWarning);
        errorEl.hidden = false;
        const text = errorEl.querySelector('[data-line-error-message]');
        (text || errorEl).textContent = message;
      }
      this.announce(message);
    }

    clearLineErrors() {
      this.querySelectorAll('[data-line-error]').forEach((errorEl) => {
        errorEl.hidden = true;
        errorEl.classList.remove('cart-page-item__error--warning');
        const text = errorEl.querySelector('[data-line-error-message]');
        (text || errorEl).textContent = '';
      });
    }

    async updateNote(note) {
      try {
        await postCart(`${this.updateUrl}.js`, { note });
      } catch (error) {
        console.error('[cart-page]', error);
      }
    }

    /* ---------- Collapsible summary rows ---------- */

    restoreCollapseState() {
      Object.keys(this.collapseState).forEach((key) => {
        const body = this.querySelector(`[data-collapse-body="${key}"]`);
        if (!body) return;
        const open = this.collapseState[key];
        body.classList.toggle('show', open);
        const toggle = this.querySelector(`[aria-controls="${CSS.escape(body.id)}"]`);
        if (toggle) {
          toggle.classList.toggle('collapsed', !open);
          toggle.setAttribute('aria-expanded', String(open));
        }
      });
    }

    /* ---------- Discount button width ---------- */

    /** Reserve trailing padding on the discount input equal to the Apply button width. */
    watchDiscountButton() {
      const input = this.querySelector('[data-discount-input]');
      const button = this.querySelector('[data-discount-apply]');
      if (this.discountObserver) this.discountObserver.disconnect();
      if (!input || !button || typeof ResizeObserver === 'undefined') return;

      this.discountObserver = new ResizeObserver(() => {
        const width = button.offsetWidth;
        // Hidden (accordion collapsed) — keep the CSS fallback.
        if (!width) return;
        input.style.setProperty('--discount-btn-width', `${width + 16}px`);
      });
      this.discountObserver.observe(button);
    }

    /* ---------- Discount codes ---------- */

    getAppliedCodes() {
      return Array.from(
        this.querySelectorAll('[data-applied-discounts] [data-discount-code]')
      ).map((el) => el.dataset.discountCode);
    }

    showDiscountError(message) {
      const errorEl = this.querySelector('[data-discount-error]');
      if (errorEl) {
        errorEl.hidden = false;
        const text = errorEl.querySelector('[data-discount-error-message]');
        (text || errorEl).textContent = message;
      }
      this.announce(message);
    }

    async applyDiscount() {
      if (this.busy) return;
      const input = this.querySelector('[data-discount-input]');
      const code = input && input.value.trim();
      if (!code) return;

      const existing = this.getAppliedCodes();
      if (existing.some((c) => c.toLowerCase() === code.toLowerCase())) {
        input.value = '';
        return;
      }

      await this.postDiscounts(existing.concat(code), { attempted: code, previous: existing });
    }

    async removeDiscount(code) {
      if (this.busy || !code) return;
      const remaining = this.getAppliedCodes().filter(
        (c) => c.toLowerCase() !== code.toLowerCase()
      );
      await this.postDiscounts(remaining);
    }
 
    async postDiscounts(codes, attempt) {
      this.setBusy(true);
      try {
        const data = await postCart(`${this.updateUrl}.js`, {
          discount: codes.join(','),
          sections: this.sections,
          sections_url: window.location.pathname,
        });

        let failed = false;
        if (attempt) {
          const entries = Array.isArray(data.discount_codes) ? data.discount_codes : null;
          if (entries) {
            const entry = entries.find(
              (d) => d.code && d.code.toLowerCase() === attempt.attempted.toLowerCase()
            );
            failed = !entry || entry.applicable === false;
          }
          if (!failed) { 
            const html = data.sections && data.sections[this.sectionId];
            if (html && !entries) {
              failed = !new DOMParser()
                .parseFromString(html, 'text/html')
                .querySelector(
                  `[data-discount-code="${CSS.escape(attempt.attempted)}" i]`
                );
            }
          }
        }

        this.renderSections(data.sections);

        if (attempt && failed) {
          this.showDiscountError(this.dataset.discountError || this.errorText);
          const input = this.querySelector('[data-discount-input]');
          if (input) {
            input.value = attempt.attempted;
            input.focus();
          } 
          postCart(`${this.updateUrl}.js`, { discount: attempt.previous.join(',') }).catch(
            (error) => console.error('[cart-page]', error)
          );
        } else if (attempt) {
          this.announce(this.dataset.discountApplied || '');
          const input = this.querySelector('[data-discount-input]');
          if (input) input.focus();
        }
      } catch (error) {
        this.showDiscountError(this.dataset.discountError || this.errorText);
        console.error('[cart-page]', error);
      } finally {
        this.setBusy(false);
      }
    }

    /* ---------- Section rendering ---------- */ 
    renderSections(sections) {
      if (!sections) return;

      const pageHtml = sections[this.sectionId];
      if (pageHtml) {
        const doc = new DOMParser().parseFromString(pageHtml, 'text/html');
        const newContent = doc.querySelector('[data-cart-content]');
        const oldContent = this.querySelector('[data-cart-content]');
        if (newContent && oldContent) {
          oldContent.replaceWith(newContent);
          this.restoreCollapseState();
          this.watchDiscountButton();
        }
      }

      const headerHtml = sections['header'];
      if (headerHtml) {
        const doc = new DOMParser().parseFromString(headerHtml, 'text/html');
        const newCount = doc.querySelector('[data-cart-count]');
        const oldCount = document.querySelector('[data-cart-count]');
        if (oldCount) {
          if (newCount) oldCount.replaceWith(newCount);
          else oldCount.remove();
        } else if (newCount) {
          const link = document.querySelector('[data-cart-drawer-open], .header-cart');
          if (link) link.prepend(newCount);
        }
      }
    }

    announce(message) {
      if (this.status && message) this.status.textContent = message;
    }
  }
 
  if (!customElements.get('quantity-stepper')) {
    customElements.define('quantity-stepper', class extends HTMLElement {});
  }
  if (!customElements.get('cart-page')) customElements.define('cart-page', CartPage);
})();
